import React, { useState, useRef } from 'react';
import axios from 'axios';
import './AIAssist.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const FIELD_TYPES = ['string', 'text', 'number', 'image', 'video', 'file', 'boolean', 'date', 'html', 'array'];

// Safe string converter
const safeStr = (val) => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};

export default function AIAssist({ onApply, onClose }) {
  const [step, setStep] = useState('upload');
  const [preview, setPreview] = useState(null);
  const [base64, setBase64] = useState(null);
  const [mediaType, setMediaType] = useState('image/png');
  const [error, setError] = useState('');
  const [sectionName, setSectionName] = useState('');
  const [fields, setFields] = useState([]);
  const fileRef = useRef();

  const handleFile = (file) => {
    if (!file) return;
    setMediaType(file.type || 'image/png');
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setPreview(dataUrl);
      setBase64(dataUrl.split(',')[1]);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  };

  const handleAnalyze = async () => {
    if (!base64) return;
    setStep('analyzing');
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/ai/analyze`, { image: base64, mediaType });
      const data = res.data;
      setSectionName(safeStr(data.sectionName) || 'new_section');
      // Sanitize all fields — ensure values are always strings
      const sanitized = (data.fields || []).map(f => ({
        name: safeStr(f.name),
        label: safeStr(f.label || f.name),
        type: safeStr(f.type) || 'string',
        value: safeStr(f.value),
        subFields: Array.isArray(f.subFields) ? f.subFields.map(sf => safeStr(sf)) : []
      }));
      setFields(sanitized);
      setStep('review');
    } catch (err) {
      setError(err.response?.data?.error || 'Analysis failed. Please try again.');
      setStep('upload');
    }
  };

  const updateField = (idx, key, val) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, [key]: safeStr(val) } : f));
  };

  const removeField = (idx) => setFields(prev => prev.filter((_, i) => i !== idx));

  const addField = () => setFields(prev => [...prev, { name: '', label: '', type: 'string', value: '', subFields: [] }]);

  const handleApply = () => {
    onApply({ sectionName, fields });
    setStep('done');
  };

  return (
    <div className="ai-overlay" onClick={onClose}>
      <div className="ai-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="ai-header">
          <div className="ai-header-left">
            <div className="ai-icon">✦</div>
            <div>
              <div className="ai-title">AI Section Builder</div>
              <div className="ai-subtitle">Upload a screenshot → AI detects fields & data</div>
            </div>
          </div>
          <button className="ai-close" onClick={onClose}>×</button>
        </div>

        {/* Steps */}
        <div className="ai-steps">
          {['Upload', 'Analyze', 'Review', 'Apply'].map((s, i) => (
            <div key={s} className={`ai-step ${['upload','analyzing','review','done'].indexOf(step) >= i ? 'active' : ''}`}>
              <div className="ai-step-dot">{i + 1}</div>
              <span>{s}</span>
            </div>
          ))}
        </div>

        {/* UPLOAD */}
        {step === 'upload' && (
          <div className="ai-body">
            {error && <div className="ai-error">{error}</div>}
            <div className={`ai-dropzone ${preview ? 'has-preview' : ''}`}
              onDrop={handleDrop} onDragOver={e => e.preventDefault()}
              onClick={() => fileRef.current?.click()}>
              {preview
                ? <img src={preview} alt="preview" className="ai-preview-img" />
                : <>
                    <div className="ai-drop-icon">🖼</div>
                    <div className="ai-drop-text">Drop a screenshot here or click to upload</div>
                    <div className="ai-drop-sub">PNG, JPG, WebP supported</div>
                  </>}
              <input ref={fileRef} type="file" accept="image/*" style={{display:'none'}}
                onChange={e => handleFile(e.target.files[0])} />
            </div>
            {preview && (
              <div className="ai-actions">
                <button className="ai-btn-ghost" onClick={() => { setPreview(null); setBase64(null); }}>Clear</button>
                <button className="ai-btn-primary" onClick={handleAnalyze}>✦ Analyze with AI</button>
              </div>
            )}
          </div>
        )}

        {/* ANALYZING */}
        {step === 'analyzing' && (
          <div className="ai-body ai-loading">
            <div className="ai-spinner" />
            <div className="ai-loading-text">Analyzing your screenshot...</div>
            <div className="ai-loading-sub">Claude is detecting fields, content, and structure</div>
          </div>
        )}

        {/* REVIEW */}
        {step === 'review' && (
          <div className="ai-body">
            <div className="ai-review-header">
              <div>
                <div className="ai-review-label">Section Name</div>
                <input className="ai-name-input" value={sectionName}
                  onChange={e => setSectionName(e.target.value.toLowerCase().replace(/\s+/g,'_').replace(/[^a-z0-9_]/g,''))}
                  placeholder="section_name" />
              </div>
              <div className="ai-review-count">{fields.length} fields detected</div>
            </div>

            <div className="ai-fields-list">
              {fields.map((f, idx) => (
                <div key={idx} className="ai-field-row">
                  <div className="ai-field-top">
                    <input className="ai-field-name" value={safeStr(f.name)}
                      onChange={e => updateField(idx, 'name', e.target.value)}
                      placeholder="field_name" />
                    <select className="ai-field-type" value={safeStr(f.type)}
                      onChange={e => updateField(idx, 'type', e.target.value)}>
                      {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <button className="ai-field-remove" onClick={() => removeField(idx)}>×</button>
                  </div>
                  {f.type !== 'array' && f.type !== 'image' && f.type !== 'video' && f.type !== 'file' && (
                    <div className="ai-field-value">
                      {f.type === 'text'
                        ? <textarea className="ai-field-val-input" rows={2}
                            value={safeStr(f.value)}
                            onChange={e => updateField(idx, 'value', e.target.value)}
                            placeholder="Detected value..." />
                        : <input className="ai-field-val-input"
                            value={safeStr(f.value)}
                            onChange={e => updateField(idx, 'value', e.target.value)}
                            placeholder="Detected value..." />
                      }
                    </div>
                  )}
                  {f.type === 'array' && Array.isArray(f.subFields) && f.subFields.length > 0 && (
                    <div className="ai-subfields">
                      Sub-fields: {f.subFields.map((sf, i) => (
                        <span key={i} className="ai-subfield-pill">{safeStr(sf)}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button className="ai-add-field" onClick={addField}>+ Add field manually</button>

            <div className="ai-actions">
              <button className="ai-btn-ghost" onClick={() => setStep('upload')}>← Back</button>
              <button className="ai-btn-primary" onClick={handleApply}>✦ Apply to CMS</button>
            </div>
          </div>
        )}

        {/* DONE */}
        {step === 'done' && (
          <div className="ai-body ai-done">
            <div className="ai-done-icon">✓</div>
            <div className="ai-done-text">Section created successfully!</div>
            <div className="ai-done-sub">
              <strong>{sectionName}</strong> with {fields.length} fields has been added to your CMS.
            </div>
            <button className="ai-btn-primary" onClick={onClose}>Close</button>
          </div>
        )}

      </div>
    </div>
  );
}
